import React from 'react';
import { Button, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatRateTimestamp } from '../utils/format';

export interface FxRateBadgeProps {
  /** USD→ZAR rate, or null when unavailable. */
  rate: number | null;
  loading: boolean;
  error: string | null;
  /** ISO timestamp the rate was fetched at (for the tooltip). */
  fetchedAt: string | null;
  /** Re-fetch the rate. */
  onRefresh: () => void;
}

/**
 * Shared USD→ZAR rate badge + refresh button. Extracted from CostReportPage so
 * the Cost Report and Referral Sources pages share one implementation
 * (per general/abstract-before-duplicating.md). Pairs with `useUsdZarRate()`.
 */
const FxRateBadge: React.FC<FxRateBadgeProps> = ({ rate, loading, error, fetchedAt, onRefresh }) => (
  <Stack direction="row" spacing={1} alignItems="center" data-testid="fx-rate-badge">
    {loading && <CircularProgress size={16} />}
    {!loading && rate != null && (
      <Tooltip title={`USD→ZAR rate from open.er-api.com · last updated ${formatRateTimestamp(fetchedAt)}`}>
        <Chip size="small" label={`R${rate.toFixed(4)} / $1`} color="primary" variant="outlined" />
      </Tooltip>
    )}
    {!loading && rate == null && (
      <Tooltip title={error || 'FX rate unavailable'}>
        <Chip size="small" label="ZAR rate unavailable" color="warning" variant="outlined" />
      </Tooltip>
    )}
    <Tooltip title="Refresh exchange rate">
      <span>
        <Button
          size="small"
          onClick={onRefresh}
          disabled={loading}
          startIcon={<RefreshIcon fontSize="small" />}
          data-testid="fx-refresh"
          sx={{ minWidth: 0 }}
        >
          FX
        </Button>
      </span>
    </Tooltip>
  </Stack>
);

export default FxRateBadge;
